import * as React from "react";
import { Component, CSSProperties, ReactNode } from "react";
import PointSelector from "../selectors/PointSelector";
import * as styles from "./Annotation.less";
import { Picture } from "./Picture";

type State = {
    showGeometry: boolean;
};

export default class Annotation extends Component<AnnotationProps, State> {
    container: HTMLImageElement;
    selectors: Selector[] = [PointSelector];

    constructor(props: AnnotationProps) {
        super(props);

        this.state = {
            showGeometry: false,
        };

        this.onTargetMouseClick = this.onTargetMouseClick.bind(this);
        this.calculatePosition = this.calculatePosition.bind(this);
    }

    setInnerRef = (el: HTMLImageElement) => {
        this.container = el;
    };

    getSelectorByType = (type: string) => {
        return this.selectors.find(s => s.TYPE === type);
    };

    getTopAnnotationAt = (x: number, y: number) => {
        const { annotations } = this.props;
        const { container, getSelectorByType } = this;

        if (!container) {
            return;
        }

        const intersections = annotations
            .map(annotation => {
                const { geometry } = annotation;
                const selector = getSelectorByType(geometry.type);

                return selector.intersects({ x, y }, geometry, container) ? annotation : false;
            })
            .filter(a => !!a)
            .sort((a: any, b: any) => {
                const aSelector = getSelectorByType(a.geometry.type);
                const bSelector = getSelectorByType(b.geometry.type);

                return aSelector.area(a.geometry, container) - bSelector.area(b.geometry, container);
            });

        return intersections[0];
    };

    onClick = (e: React.MouseEvent<HTMLElement>) => this.callSelectorMethod("onClick", e);

    onSubmit = () => {
        this.props.onSubmit(this.props.value);
    };

    onTargetMouseClick = (e: React.MouseEvent<HTMLElement>) => {
        const mousePositionPercentage = this.calculatePosition(e);
        const activeAnnotation = this.getTopAnnotationAt(mousePositionPercentage.x, mousePositionPercentage.y);
        if (!activeAnnotation) {
            this.onClick(e);
        } else {
            this.props.onOpen(activeAnnotation);
        }
    };

    calculatePosition = (e: React.MouseEvent<HTMLElement>) => {
        return {
            x: (e.nativeEvent.offsetX / this.container.width) * 100,
            y: (e.nativeEvent.offsetY / this.container.height) * 100,
        };
    };

    callSelectorMethod = (methodName: string, e: React.MouseEvent<HTMLElement>) => {
        if (this.props.disableAnnotation) {
            return;
        }

        if (!!this.props[methodName]) {
            this.props[methodName](e);
        } else {
            const selector = this.getSelectorByType(this.props.type);
            if (selector && selector.methods[methodName]) {
                const value = selector.methods[methodName]({}, e);

                if (typeof value === "undefined") {
                    if (process.env.NODE_ENV !== "production") {
                        throw new Error(`
                        ${methodName} of selector type ${this.props.type} returned undefined.
                        Make sure to explicitly return the previous state
                                        `);
                    }
                } else {
                    this.props.onChange(value);
                }
            }
        }
    };

    shouldAnnotationBeActive = (annotation: AnnotationItem) => {
        if (this.props.value && this.props.value.data) {
            const isActive = !!this.props.activeAnnotationComparator(annotation, this.props.value);

            return isActive;
        }
        return false;
    };

    onLoaded = () => {
        this.setState({ showGeometry: true });
    };

    render() {
        const { props } = this;
        const { renderHighlight, renderContent, renderSelector, renderEditor, renderOverlay } = props;

        const { showGeometry } = this.state;

        return (
            <div className={styles.container}>
                <Picture
                    loading={!showGeometry}
                    onLoaded={this.onLoaded}
                    alt={props.alt}
                    src={props.src}
                    setInnerRef={this.setInnerRef}
                />
                {showGeometry && (
                    <>
                        <div className={styles.items_point}>
                            {props.annotations.map(annotation =>
                                renderHighlight({
                                    key: annotation.data.id,
                                    annotation,
                                    active: this.shouldAnnotationBeActive(annotation),
                                })
                            )}
                            {!props.disableSelector &&
                                props.value &&
                                props.value.geometry &&
                                renderSelector({
                                    annotation: props.value,
                                })}
                        </div>
                        <div className={styles.items} onClick={this.onTargetMouseClick} />
                        {!props.disableOverlay &&
                            renderOverlay({
                                type: props.type,
                                annotation: props.value,
                            })}
                        {props.annotations.map(
                            annotation =>
                                props.value &&
                                !props.value.selection &&
                                this.shouldAnnotationBeActive(annotation) &&
                                renderContent({
                                    key: annotation.data.id,
                                    annotation,
                                })
                        )}
                        {!props.disableEditor &&
                            props.value &&
                            props.value.selection &&
                            props.value.selection.showEditor &&
                            renderEditor({
                                annotation: props.value,
                                onChange: props.onChange,
                                onSubmit: this.onSubmit,
                            })}

                        <div>{props.children}</div>
                    </>
                )}
            </div>
        );
    }
}

export interface Geometry {
    type: string;
    x: number;
    y: number;
    width: number;
    height: number;
}

export interface Selector {
    TYPE?: string;
    intersects: ({ x, y }: any, geometry: object, container: object) => any;
    area: (geometry: object, container: object) => number;
    methods: any;
    showEditor: boolean;
    showEditorInline: boolean;
    mode?: string;
}

export interface DataObject {
    text?: string;
    id?: number;
    hover?: boolean;
}

export interface AnnotationItem {
    selection?: Selector;
    geometry?: Geometry;
    data?: DataObject;
}

export type AnnotationItems = AnnotationItem[];
export type SetAnnotationFunc = (annotation: AnnotationItem) => void;

interface Params {
    annotation: AnnotationItem;
}

export type RenderSelectorParams = Params;

export interface RenderEditorParams extends Params {
    onChange: SetAnnotationFunc;
    onSubmit: SetAnnotationFunc;
}

export interface RenderHighlightParams extends Params {
    key: number;
    active: boolean;
}

export interface RenderContentParams extends Params {
    key: number;
}

export interface RenderOverlayParams extends Params {
    type: string;
}

export interface AnnotationProps {
    style?: CSSProperties;
    innerRef?: (el: HTMLElement) => void;
    onChange?: SetAnnotationFunc;
    onSubmit?: SetAnnotationFunc;
    onMouseUp?: (el: React.MouseEvent<HTMLElement>) => void;
    onMouseDown?: (el: React.MouseEvent<HTMLElement>) => void;
    onMouseMove?: (el: React.MouseEvent<HTMLElement>) => void;
    onClick?: (el: React.MouseEvent<HTMLElement>) => void;
    src?: string;
    alt?: string;
    type?: string;
    selectors?: Selector[];
    annotations: AnnotationItems;
    value: AnnotationItem;
    activeAnnotations?: AnnotationItems;
    activeAnnotationComparator?: (a: AnnotationItem, b: AnnotationItem) => boolean;
    disableAnnotation?: boolean;
    disableSelector?: boolean;
    disableEditor?: boolean;
    disableOverlay?: boolean;
    renderSelector?: (params?: RenderSelectorParams) => ReactNode;
    renderEditor?: (params?: RenderEditorParams) => ReactNode;
    renderHighlight?: (params?: RenderHighlightParams) => ReactNode;
    renderContent?: (params?: RenderContentParams) => ReactNode;
    renderOverlay?: (params?: RenderOverlayParams) => ReactNode;

    [key: string]: any;
}
